if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      ProductForm.installNativeSubmitGuard();
      ProductForm.installCartAddSubmitGuard();
      if (this.hasChildNodes()) this.init();
    }

    connectedCallback() {
      setTimeout(() => {
        if (!this.form) this.init();
      }, 0);
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (!this.form.dataset.productFormAjaxReady) {
          this.form.dataset.productFormAjaxReady = 'true';
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (!this.submitBtn || this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      let addedSuccessfully = false;
      try {
        // Single cart call per add: the bundled-section response (see Shopify's
        // Section Rendering API) already contains everything the drawer needs,
        // so no extra /cart.js round-trips are required.
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = ProductForm.getCartError(data);
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);
        addedSuccessfully = true;

        const forceCartDrawer = this.cartDrawer && theme.settings.cartType === 'drawer';
        if (theme.settings.afterAtc === 'page' && !forceCartDrawer) {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          document.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: data.variant_id
            }
          }));

          // Open the drawer as soon as the cart responds (the button tick
          // animation still runs; the old 700ms/200ms pauses only added delay).
          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                forceCartDrawer || (!quickAddDrawer && theme.settings.afterAtc === 'drawer')
              ).catch(() => this.cartDrawer.refresh(true));
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 0);
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        if (addedSuccessfully) this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    static installNativeSubmitGuard() {
      if (window.__mpsProductFormSubmitGuardInstalled) return;
      window.__mpsProductFormSubmitGuardInstalled = true;

      const nativeSubmit = HTMLFormElement.prototype.submit;
      HTMLFormElement.prototype.submit = function submit() {
        if ((this.matches('.js-product-form') && this.closest('product-form')) || ProductForm.isCartAddForm(this)) {
          const event = typeof SubmitEvent === 'function'
            ? new SubmitEvent('submit', { bubbles: true, cancelable: true })
            : new Event('submit', { bubbles: true, cancelable: true });
          if (!this.dispatchEvent(event)) return;
        }

        return nativeSubmit.call(this);
      };
    }

    static installCartAddSubmitGuard() {
      if (window.__mpsCartAddSubmitGuardInstalled) return;
      window.__mpsCartAddSubmitGuardInstalled = true;

      document.addEventListener('submit', (evt) => {
        const form = evt.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form.closest('product-form')) return;
        if (!ProductForm.isCartAddForm(form)) return;

        ProductForm.handleLooseCartAdd(evt, form);
      }, true);
    }

    static isCartAddForm(form) {
      if (!(form instanceof HTMLFormElement)) return false;

      try {
        const action = new URL(form.getAttribute('action') || form.action || '', window.location.origin);
        return action.pathname.replace(/\/+$/, '') === theme.routes.cartAdd.replace(/\/+$/, '');
      } catch (error) {
        return false;
      }
    }

    static async handleLooseCartAdd(evt, form) {
      evt.preventDefault();
      if (form.dataset.mpsAjaxAdding === 'true') return;

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      const submitBtn = evt.submitter || form.querySelector('[name="add"]');
      const cartDrawer = document.querySelector('cart-drawer');
      const errorMsg = form.querySelector('.js-form-error');
      form.dataset.mpsAjaxAdding = 'true';
      submitBtn?.setAttribute('aria-disabled', 'true');
      submitBtn?.classList.add('is-loading');
      if (errorMsg) {
        errorMsg.hidden = true;
        errorMsg.textContent = '';
      }

      const formData = new FormData(form);
      const sections = ['cart-icon-bubble'];
      const cartDrawerSection = cartDrawer?.closest('.shopify-section')?.id?.replace('shopify-section-', '');
      if (cartDrawerSection) sections.push(cartDrawerSection);
      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections.join(','));

      let addedSuccessfully = false;
      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });
        const data = await response.json();
        if (data.status || !response.ok) {
          throw new Error(ProductForm.getCartError(data) || response.status);
        }

        addedSuccessfully = true;
        ProductForm.updateCartIcon(data);
        if (cartDrawer && data.sections) {
          await cartDrawer.renderContents(data, true).catch(() => cartDrawer.refresh(true));
        } else if (window.location.pathname === theme.routes.cart) {
          document.querySelector('cart-items')?.refresh?.();
        }

        document.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: {
            variantId: data.variant_id
          }
        }));
      } catch (error) {
        if (errorMsg) {
          errorMsg.textContent = error.message;
          errorMsg.hidden = false;
        }
        if (cartDrawer) cartDrawer.refresh(true);
      } finally {
        delete form.dataset.mpsAjaxAdding;
        if (addedSuccessfully) submitBtn?.classList.add('is-success');
        submitBtn?.removeAttribute('aria-disabled');
        setTimeout(() => {
          submitBtn?.classList.remove('is-loading');
          submitBtn?.classList.remove('is-success');
        }, 1400);
      }
    }

    static getCartError(data) {
      if (typeof data.description === 'string') return data.description;
      if (typeof data.message === 'string') return data.message;
      if (data.errors && typeof data.errors === 'object') {
        return Object.values(data.errors).flat().join(', ');
      }
      return '';
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      const html = response.sections && response.sections['cart-icon-bubble'];
      if (!cartIconBubble || typeof html !== 'string') return;
      const template = document.createElement('template');
      template.innerHTML = html;
      const section = template.content.querySelector('.shopify-section');
      cartIconBubble.innerHTML = section ? section.innerHTML : html;
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  ProductForm.installNativeSubmitGuard();
  ProductForm.installCartAddSubmitGuard();
  customElements.define('product-form', ProductForm);
}
